import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import auditService from '../../services/auditService'
import { formatDateTime } from '../../utils/formatters'

export const AuditPage = () => {
  // State for filters
  const [selectedAction, setSelectedAction] = useState<string>('')
  const [selectedModel, setSelectedModel] = useState<string>('')

  // Fetch audit logs
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs', selectedAction, selectedModel],
    queryFn: () =>
      auditService.getAll({
        action: selectedAction || undefined,
        model_name: selectedModel || undefined,
        ordering: '-timestamp',
      }),
  })

  // Get action badge color
  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      create: 'bg-success/10 text-success',
      update: 'bg-primary/10 text-primary',
      delete: 'bg-danger/10 text-danger',
      reverse: 'bg-warning/10 text-warning',
      cycling: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    }
    return colors[action] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }

  // Get action label
  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      create: 'Criação',
      update: 'Atualização',
      delete: 'Exclusão',
      reverse: 'Reversão',
      cycling: 'Ciclagem',
    }
    return labels[action] || action
  }

  // Get model label
  const getModelLabel = (modelName: string) => {
    const labels: Record<string, string> = {
      monthlyscore: 'Avaliação Mensal',
      employee: 'Funcionário',
      company: 'Empresa',
      pointscycling: 'Ciclagem de Pontos',
      attitudecategory: 'Categoria de Atitude',
    }
    return labels[modelName.toLowerCase()] || modelName
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Auditoria
      </h1>

      {/* Info Banner */}
      <div className="bg-primary/10 border border-primary/20 rounded-card p-6 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📋</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Log de Auditoria
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Registro de todas as ações realizadas no sistema. Use os filtros abaixo para
              encontrar ações específicas.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Action Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Ação
            </label>
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todas as ações</option>
              <option value="create">Criação</option>
              <option value="update">Atualização</option>
              <option value="delete">Exclusão</option>
              <option value="reverse">Reversão</option>
              <option value="cycling">Ciclagem</option>
            </select>
          </div>

          {/* Model Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Registro
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos os registros</option>
              <option value="MonthlyScore">Avaliação Mensal</option>
              <option value="Employee">Funcionário</option>
              <option value="Company">Empresa</option>
              <option value="PointsCycling">Ciclagem de Pontos</option>
              <option value="AttitudeCategory">Categoria de Atitude</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4 text-sm text-gray-600 dark:text-gray-400">
          <span>
            <strong className="text-gray-900 dark:text-white">{logs.length}</strong> registros encontrados
          </span>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando logs...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && logs.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Nenhum registro encontrado
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Tente ajustar os filtros para ver mais resultados.
          </p>
        </div>
      )}

      {/* Logs List */}
      {!isLoading && logs.length > 0 && (
        <div className="space-y-4">
          {logs.map((log) => (
            <div
              key={log.id}
              className="bg-white dark:bg-gray-800 rounded-card shadow p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3 flex-1">
                  {/* Action Badge */}
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getActionColor(log.action)}`}>
                    {getActionLabel(log.action)}
                  </span>

                  {/* Details */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {getModelLabel(log.model_name)}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-500">
                        #{log.object_id}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {log.description}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span>
                        👤 {log.user_detail?.username || `User #${log.user}`}
                      </span>
                      <span>
                        🕐 {formatDateTime(log.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Preview (optional expansion) */}
              {(log.data_before || log.data_after) && (
                <details className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary">
                    Ver dados técnicos
                  </summary>
                  <div className="mt-3 space-y-2">
                    {log.data_before && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-1">
                          Antes:
                        </p>
                        <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto">
                          {JSON.stringify(log.data_before, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.data_after && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-1">
                          Depois:
                        </p>
                        <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto">
                          {JSON.stringify(log.data_after, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AuditPage
